import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useUseSearchResolvedUsersQuery } from '../users/api';

import Styles from './components/styles.module.scss';
import { ToUsers } from './components/ToUsers';
import { UsersListPicker } from './components/UsersListPicker';
import { toFormValuesModel, toMessageCreateModel } from './model/mappers/form-values.model';

import type { FormValuesModel } from './model/types/form-value.model';
import type { MessageCreateModel } from '../model/types/message-create.model';
import type { UserModel } from '../users/model/types/users.model';
import type { DefaultValues, SubmitHandler } from 'react-hook-form';

import { Button } from '@/components/Elements/Button';

interface Props<T extends MessageCreateModel = MessageCreateModel> {
	value?: DefaultValues<T>;
	onSubmit: SubmitHandler<T>;
	title: string;
	buttonText?: string;
}

export const CreateFormLayout = <T extends MessageCreateModel>({
	value,
	onSubmit,
	title,
	buttonText = 'Create Message',
}: Props<T>) => {
	const { data: usersById } = useUseSearchResolvedUsersQuery();

	const options = useForm<FormValuesModel>({
		defaultValues: toFormValuesModel(value as T),
	});

	const onSubmitHandler: SubmitHandler<FormValuesModel> = (data) => {
		const users: UserModel[] = (data.users ?? [])
			.map((userId) => usersById?.[userId])
			.filter(Boolean) as UserModel[];
		const messageCreateModel = toMessageCreateModel(data, users ?? []) as T;
		return onSubmit(messageCreateModel);
	};

	return (
		<FormProvider {...options}>
			<form className={Styles.container} onSubmit={options.handleSubmit(onSubmitHandler)}>
				<section>
					<h1>{title}</h1>
					<div className={Styles.content}>
						<div className={Styles.formBody}>
							<div className={Styles.recipientGroup}>
								<span>To:</span>
								<Controller
									control={options.control}
									name={'users'}
									render={({ field }) => {
										return (
											<ToUsers
												value={field.value ?? []}
												onChange={(value) => {
													field.onChange(value);
												}}
												renderUserName={(userId) =>
													`${usersById?.[userId]?.firstName ?? ''} ${usersById?.[userId]?.lastName ?? ''}`
												}
											/>
										);
									}}
								/>
							</div>
							<div className={Styles.inputGroup}>
								<Controller
									control={options.control}
									name={'title'}
									render={({ field }) => (
										<input
											placeholder="Title"
											value={field.value ?? ''}
											onChange={field.onChange}
										/>
									)}
								/>
								<Controller
									control={options.control}
									name={'body'}
									render={({ field }) => (
										<textarea
											value={field.value ?? ''}
											onChange={field.onChange}
											wrap="soft"
											placeholder="Body"
											maxLength={107}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
												}
											}}
										/>
									)}
								/>
							</div>
							<div className={Styles.footer}>
								<Button disabled={options.formState.isSubmitting} type="submit">
									{buttonText}
								</Button>
							</div>
						</div>
					</div>
				</section>
				<section>
					<h1>Assign Users</h1>
					<div className={Styles.content}>
						<Controller
							control={options.control}
							name={'users'}
							render={({ field }) => {
								return (
									<UsersListPicker
										value={field.value ?? []}
										onChange={(value) => {
											field.onChange(value);
										}}
									/>
								);
							}}
						/>
					</div>
				</section>
			</form>
		</FormProvider>
	);
};
